import React, { useMemo, useState } from 'react';
import { gql, useApolloClient, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@material-ui/core';

import { exportProgressDocumentToPdf } from '../../PlanBuilder/services/PlanDocumentPdfService';
import { generatePlanDocumentByType } from '../../PlanBuilder/services/DocumentGeneratorService';

const ADMIN_PLAN_BUILDER_PROGRESS_INDEX_QUERY = gql`
  query AdminPlanBuilderProgressIndex(
    $planTemplateId: ID
    $status: String
    $userEmail: String
    $limit: Int
    $offset: Int
  ) {
    adminPlanTemplates {
      id
      name
      slug
      planType
      status
    }
    adminPlanBuilderProgressMetrics(
      planTemplateId: $planTemplateId
      status: $status
      userEmail: $userEmail
    ) {
      total_started
      active_count
      paused_count
      completed_count
      completion_rate
      median_completion_percentage_active
      avg_time_to_complete_seconds
      upcoming_resumes_next_24h
      reminder_status_scheduled
      reminder_status_failed
      reminder_status_disabled
    }
    adminPlanBuilderProgressesPaged(
      planTemplateId: $planTemplateId
      status: $status
      userEmail: $userEmail
      limit: $limit
      offset: $offset
    ) {
      total_count
      nodes {
        id
        status
        ceStatus
        completionPercentage
        updatedAt
        pausedAt
        scheduledResumeAt
        resumeReminderStatus
        resumeReminderLastError
        user {
          id
          name
          email
        }
        planTemplate {
          id
          name
          slug
          planType
        }
        userPlanResponses {
          id
        }
      }
    }
  }
`;

const ADMIN_PLAN_BUILDER_PROGRESS_EXPORT_QUERY = gql`
  query AdminPlanBuilderProgressExport($id: ID!) {
    adminUserPlanProgress(id: $id) {
      id
      status
      completionPercentage
      planTemplate {
        id
        name
        slug
        planType
        description
        planModules {
          id
          title
          planSections {
            id
            title
            planQuestions {
              id
              questionText
              widgetType
              options
              purpose
              category
              outputSection
              gapAction
              metadata
            }
          }
        }
      }
      user {
        id
        name
        allowed_name
        email
        company {
          name
        }
      }
      userPlanResponses {
        id
        answer
        planQuestionId
      }
    }
  }
`;

const secondsToHuman = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const minutes = Math.round(seconds / 60);
  if (minutes < 120) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} hr`;
  const days = Math.round(hours / 24);
  return `${days} d`;
};

const percent = (value) => `${Math.round((value || 0) * 1000) / 10}%`;

const toTitle = (s) => {
  const raw = String(s || '').trim();
  if (!raw) return '—';
  return raw
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const statusChipProps = (status) => {
  const s = String(status || '');
  if (s === 'completed') return { label: 'Completed', color: 'primary', variant: 'default' };
  if (s === 'paused') return { label: 'Paused', color: 'default', variant: 'default' };
  if (s === 'in_progress') return { label: 'In Progress', color: 'secondary', variant: 'default' };
  if (s === 'not_started') return { label: 'Not Started', color: 'default', variant: 'outlined' };
  return { label: toTitle(s), color: 'default', variant: 'outlined' };
};

const reminderChipProps = (status) => {
  const s = String(status || '');
  if (!s) return { label: '—', color: 'default', variant: 'outlined' };
  if (s === 'scheduled') return { label: 'Scheduled', color: 'primary', variant: 'default' };
  if (s === 'pending') return { label: 'Pending', color: 'secondary', variant: 'default' };
  if (s === 'failed') return { label: 'Failed', color: 'secondary', variant: 'outlined' };
  if (s === 'disabled') return { label: 'Disabled', color: 'default', variant: 'outlined' };
  return { label: toTitle(s), color: 'default', variant: 'outlined' };
};

const ceChipProps = (status) => {
  const s = String(status || '');
  if (!s || s === '—') return { label: '—', color: 'default', variant: 'outlined' };
  if (s.toLowerCase() === 'passed') return { label: 'Passed', color: 'primary', variant: 'default' };
  if (s.toLowerCase() === 'failed') return { label: 'Failed', color: 'secondary', variant: 'outlined' };
  if (s.toLowerCase() === 'not started') return { label: 'Not Started', color: 'default', variant: 'outlined' };
  if (s.toLowerCase() === 'n/a') return { label: 'N/A', color: 'default', variant: 'outlined' };
  return { label: toTitle(s), color: 'default', variant: 'outlined' };
};

const CHIP_STYLE = {
  height: 22,
  fontSize: 11,
  fontWeight: 600,
};

const CHIP_STYLE_INFO = {
  ...CHIP_STYLE,
  backgroundColor: '#E3F2FD',
  color: '#0D47A1',
  border: '1px solid #90CAF9',
};

const CHIP_STYLE_SUCCESS = {
  ...CHIP_STYLE,
  backgroundColor: '#2E7D32',
  color: '#FFFFFF',
};

const CHIP_STYLE_MUTED = {
  ...CHIP_STYLE,
  backgroundColor: '#F3F4F6',
  color: '#374151',
  border: '1px solid #D1D5DB',
};

const withChipStyle = (props, style) => ({ ...props, style: { ...(props.style || {}), ...(style || {}) } });

const statusChip = (status) => {
  const s = String(status || '');
  if (s === 'completed') return withChipStyle({ label: 'Completed', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s === 'in_progress') return withChipStyle({ label: 'In Progress', variant: 'default' }, CHIP_STYLE_INFO);
  if (s === 'paused') return withChipStyle({ label: 'Paused', variant: 'outlined' }, CHIP_STYLE);
  if (s === 'not_started') return withChipStyle({ label: 'Not Started', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const reminderChip = (status) => {
  const s = String(status || '');
  if (!s) return withChipStyle({ label: '—', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s === 'scheduled') return withChipStyle({ label: 'Scheduled', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s === 'pending') return withChipStyle({ label: 'Pending', variant: 'default' }, CHIP_STYLE_INFO);
  if (s === 'failed') return withChipStyle({ label: 'Failed', variant: 'outlined' }, CHIP_STYLE);
  if (s === 'disabled') return withChipStyle({ label: 'Disabled', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const ceChip = (status) => {
  const s = String(status || '');
  if (!s || s === '—') return withChipStyle({ label: '—', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s.toLowerCase() === 'passed') return withChipStyle({ label: 'Passed', variant: 'default' }, CHIP_STYLE_SUCCESS);
  if (s.toLowerCase() === 'failed') return withChipStyle({ label: 'Failed', variant: 'outlined' }, CHIP_STYLE);
  if (s.toLowerCase() === 'not started') return withChipStyle({ label: 'Not Started', variant: 'outlined' }, CHIP_STYLE_MUTED);
  if (s.toLowerCase() === 'n/a') return withChipStyle({ label: 'N/A', variant: 'outlined' }, CHIP_STYLE_MUTED);
  return withChipStyle({ label: toTitle(s), variant: 'outlined' }, CHIP_STYLE);
};

const PlanBuilderProgressIndex = () => {
  const apolloClient = useApolloClient();
  const [planTemplateId, setPlanTemplateId] = useState('');
  const [status, setStatus] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [offset, setOffset] = useState(0);
  const [downloadingById, setDownloadingById] = useState({});
  const limit = 25;

  const { data, loading, error, refetch } = useQuery(ADMIN_PLAN_BUILDER_PROGRESS_INDEX_QUERY, {
    variables: {
      planTemplateId: planTemplateId || null,
      status: status || null,
      userEmail: userEmail || null,
      limit,
      offset,
    },
    fetchPolicy: 'cache-and-network',
  });

  const templates = data?.adminPlanTemplates || [];
  const metrics = data?.adminPlanBuilderProgressMetrics;
  const paged = data?.adminPlanBuilderProgressesPaged;
  const rows = paged?.nodes || [];
  const total = paged?.total_count || 0;

  const page = Math.floor(offset / limit) + 1;
  const maxPage = Math.max(1, Math.ceil(total / limit));

  const templateById = useMemo(() => {
    const m = new Map();
    templates.forEach((t) => m.set(String(t.id), t));
    return m;
  }, [templates]);

  const activeTemplateLabel = planTemplateId ? templateById.get(String(planTemplateId))?.name : '';

  const exportPdfInBackground = async (progressId) => {
    if (!progressId) return;
    setDownloadingById((prev) => ({ ...prev, [progressId]: true }));
    try {
      const { data: exportData } = await apolloClient.query({
        query: ADMIN_PLAN_BUILDER_PROGRESS_EXPORT_QUERY,
        variables: { id: progressId },
        fetchPolicy: 'network-only',
      });

      const progress = exportData?.adminUserPlanProgress;
      if (!progress) throw new Error('Missing progress payload');

      const planName = progress?.planTemplate?.name || 'Succession Plan';
      const userName = progress?.user?.allowed_name || progress?.user?.name || 'User';
      const companyName = progress?.user?.company?.name || 'Practice';
      const filename = `${userName.replace(/\s/g, '_')}_${planName.replace(/\s/g, '_')}.pdf`;

      await exportProgressDocumentToPdf({
        content: generatePlanDocumentByType(progress, userName, companyName),
        userName,
        companyName,
        filename,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Admin background export failed:', e);
      window.alert(`Failed to export PDF: ${e.message || e}`);
    } finally {
      setDownloadingById((prev) => ({ ...prev, [progressId]: false }));
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Plan Builder Progress</Typography>

      {error && (
        <Box mb={2}>
          <Typography color="error">Error loading data: {error.message}</Typography>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Active</Typography>
              <Typography variant="h5">{metrics?.active_count ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Paused</Typography>
              <Typography variant="h5">{metrics?.paused_count ?? '—'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Completed</Typography>
              <Typography variant="h5">{metrics?.completed_count ?? '—'}</Typography>
              <Typography variant="body2" color="textSecondary">
                Completion rate: {metrics ? percent(metrics.completion_rate) : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">Upcoming resumes (24h)</Typography>
              <Typography variant="h5">{metrics?.upcoming_resumes_next_24h ?? '—'}</Typography>
              <Typography variant="body2" color="textSecondary">
                Avg time-to-complete: {secondsToHuman(metrics?.avg_time_to_complete_seconds)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={3} mb={2}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="plan-template-label">Template</InputLabel>
              <Select
                labelId="plan-template-label"
                value={planTemplateId}
                onChange={(e) => { setPlanTemplateId(e.target.value); setOffset(0); }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name} ({t.status})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                value={status}
                onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="in_progress">in_progress</MenuItem>
                <MenuItem value="paused">paused</MenuItem>
                <MenuItem value="completed">completed</MenuItem>
                <MenuItem value="not_started">not_started</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="User email contains"
              value={userEmail}
              onChange={(e) => { setUserEmail(e.target.value); setOffset(0); }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => refetch()}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          Showing page {page} of {maxPage}
          {activeTemplateLabel ? ` • Template: ${activeTemplateLabel}` : ''}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Reminder health: scheduled {metrics?.reminder_status_scheduled ?? '—'} • failed {metrics?.reminder_status_failed ?? '—'} • disabled {metrics?.reminder_status_disabled ?? '—'}
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Template</TableCell>
              <TableCell align="center">Actions</TableCell>
              <TableCell>CE</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell align="right">Answers</TableCell>
              <TableCell>Paused</TableCell>
              <TableCell>Resume</TableCell>
              <TableCell>Reminder</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((p) => (
              (() => {
                const isCompleted = p.status === 'completed';
                const isDownloading = !!downloadingById[p.id];
                const canDownload = isCompleted && !isDownloading;

                return (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Link to={`/admin/plan_builder/progress/${p.id}`}>
                    {p.user?.name || `User ${p.user?.id || ''}`}
                  </Link>
                  {p.user?.email ? (
                    <Typography variant="caption" display="block" color="textSecondary">{p.user.email}</Typography>
                  ) : null}
                </TableCell>
                <TableCell>{p.planTemplate?.name || p.planTemplate?.slug || '—'}</TableCell>
                <TableCell align="center">
                  <button
                    type="button"
                    onClick={() => exportPdfInBackground(p.id)}
                    disabled={!canDownload}
                    title={isCompleted ? 'Download PDF' : 'Available when completed'}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      border: 'none',
                      background: 'transparent',
                      cursor: canDownload ? 'pointer' : 'not-allowed',
                      opacity: canDownload ? 1 : 0.35,
                    }}
                  >
                    <i className={isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-download'} />
                  </button>
                </TableCell>
                <TableCell>
                  <Chip size="small" {...ceChip(p.ceStatus || '—')} />
                </TableCell>
                <TableCell>
                  <Chip size="small" {...statusChip(p.status)} />
                </TableCell>
                <TableCell align="right">{p.completionPercentage}</TableCell>
                <TableCell align="right">{p.userPlanResponses?.length || 0}</TableCell>
                <TableCell>{p.pausedAt ? new Date(p.pausedAt).toLocaleString() : '—'}</TableCell>
                <TableCell>{p.scheduledResumeAt ? new Date(p.scheduledResumeAt).toLocaleString() : '—'}</TableCell>
                <TableCell>
                  <Chip size="small" {...reminderChip(p.resumeReminderStatus)} />
                  {p.resumeReminderStatus === 'failed' && p.resumeReminderLastError ? (
                    <Typography variant="caption" display="block" color="error">{p.resumeReminderLastError}</Typography>
                  ) : null}
                </TableCell>
                <TableCell>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}</TableCell>
              </TableRow>
                );
              })()
            ))}
            {!rows.length && !loading && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Typography variant="body2" color="textSecondary">No results.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2} display="flex" justifyContent="space-between">
        <Button
          onClick={() => setOffset(Math.max(0, offset - limit))}
          disabled={offset <= 0}
        >
          Prev
        </Button>
        <Button
          onClick={() => setOffset(Math.min((maxPage - 1) * limit, offset + limit))}
          disabled={page >= maxPage}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default PlanBuilderProgressIndex;


